import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import * as client from "../../../client";
import { useRerunOptions } from "../data";
import styles from "./ReprocessDialog.module.css";

type Mode = "full" | "select";

const COMPONENT_LABELS: Record<client.RerunComponent, string> = {
  sov: "SOV / address parsing",
  loss_run: "Loss run parsing",
  loss_control: "Loss control reports",
  policy_terms: "Limits, deductibles & sublimits",
  policy_questions: "Policy questions",
};

export type ReprocessSelection = {
  components: client.RerunComponent[];
  attachments: string[];
};

type ReprocessDialogProps = {
  reportId: string;
  visible: boolean;
  onHide: () => void;
  // ``undefined`` means a full reprocess; otherwise the selected components,
  // narrowed to the selected attachments.
  onConfirm: (selection: ReprocessSelection | undefined) => void;
  isPending: boolean;
};

/**
 * Attachments a scoped rerun should start with: the ones the report holds no
 * parsed data for yet (typically what was just uploaded).
 */
const unparsedAttachments = (
  components: client.RerunComponentOptions[]
): Set<string> =>
  new Set(
    components.flatMap((c) =>
      c.attachments.filter((a) => !a.is_parsed).map((a) => a.name)
    )
  );

export const ReprocessDialog: React.FC<ReprocessDialogProps> = ({
  reportId,
  visible,
  onHide,
  onConfirm,
  isPending,
}) => {
  const [mode, setMode] = useState<Mode>("full");
  // The user's checkbox edits as attachment names, or null while they have
  // made none, in which case the unparsed attachments are checked. The
  // default stays derived rather than copied into state, so the options
  // loading or refetching while the dialog is open never discards edits. An
  // attachment feeding several components (a specs sheet, an ACORD app) is
  // one checkbox per component but one entry here: checking it anywhere
  // re-parses it everywhere it feeds, which is what the backend does with a
  // named attachment anyway.
  const [edits, setEdits] = useState<Set<string> | null>(null);
  const options = useRerunOptions(reportId, visible);
  const components = useMemo(
    () => options.data?.components ?? [],
    [options.data]
  );
  const checked = useMemo(
    () => edits ?? unparsedAttachments(components),
    [edits, components]
  );

  useEffect(() => {
    setMode("full");
    setEdits(null);
  }, [visible]);

  const updateChecked = (change: (next: Set<string>) => void) => {
    setEdits((prev) => {
      const next = new Set(prev ?? unparsedAttachments(components));
      change(next);
      return next;
    });
  };

  const toggleAttachment = (name: string, on: boolean) => {
    updateChecked((next) => {
      if (on) {
        next.add(name);
      } else {
        next.delete(name);
      }
    });
  };

  const toggleComponent = (
    component: client.RerunComponentOptions,
    on: boolean
  ) => {
    updateChecked((next) => {
      for (const attachment of component.attachments) {
        if (on) {
          next.add(attachment.name);
        } else {
          next.delete(attachment.name);
        }
      }
    });
  };

  const selection = useMemo((): ReprocessSelection => {
    const selectedComponents = components
      .filter((c) => c.attachments.some((a) => checked.has(a.name)))
      .map((c) => c.component);
    return { components: selectedComponents, attachments: [...checked] };
  }, [components, checked]);

  const confirmDisabled =
    isPending || (mode === "select" && selection.components.length === 0);

  const footer = (
    <div className={styles.footer}>
      <Button
        label="Cancel"
        severity="secondary"
        text
        onClick={onHide}
        disabled={isPending}
      />
      <Button
        label="Reprocess"
        icon="pi pi-replay"
        loading={isPending}
        disabled={confirmDisabled}
        onClick={() => onConfirm(mode === "full" ? undefined : selection)}
      />
    </div>
  );

  return (
    <Dialog
      header="Reprocess submission"
      visible={visible}
      onHide={onHide}
      draggable={false}
      footer={footer}
      style={{ width: "32rem" }}
    >
      <div className={styles.container}>
        <div className={styles.option}>
          <RadioButton
            inputId="reprocess-full"
            checked={mode === "full"}
            onChange={() => setMode("full")}
          />
          <label htmlFor="reprocess-full" className={styles.optionLabel}>
            <div className={styles.optionTitle}>Full reprocess</div>
            <div className={styles.optionDescription}>
              Re-parse the entire email and every attachment.
            </div>
          </label>
        </div>

        <div className={styles.option}>
          <RadioButton
            inputId="reprocess-select"
            checked={mode === "select"}
            onChange={() => setMode("select")}
          />
          <label htmlFor="reprocess-select" className={styles.optionLabel}>
            <div className={styles.optionTitle}>Select attachments</div>
            <div className={styles.optionDescription}>
              Only re-parse the checked attachments; everything else the report
              already holds is preserved. Attachments that have not been parsed
              yet start checked.
            </div>
          </label>
        </div>

        {mode === "select" && (
          <div className={styles.componentList}>
            {options.isLoading && (
              <div className={styles.optionDescription}>
                Loading attachments…
              </div>
            )}
            {options.isError && (
              <div className={styles.optionDescription}>
                Could not load the report's attachments.
              </div>
            )}
            {components.map((component) => {
              const checkedCount = component.attachments.filter((a) =>
                checked.has(a.name)
              ).length;
              const allChecked =
                component.attachments.length > 0 &&
                checkedCount === component.attachments.length;
              const inputId = `reprocess-${component.component}`;
              return (
                <div
                  key={component.component}
                  className={styles.componentGroup}
                >
                  <div className={styles.componentItem}>
                    <Checkbox
                      inputId={inputId}
                      checked={allChecked}
                      disabled={component.attachments.length === 0}
                      className={
                        checkedCount > 0 && !allChecked
                          ? styles.partiallyChecked
                          : undefined
                      }
                      onChange={(e) => toggleComponent(component, !!e.checked)}
                    />
                    <label htmlFor={inputId}>
                      {COMPONENT_LABELS[component.component]}
                    </label>
                    {component.attachments.length === 0 && (
                      <span className={styles.attachmentNote}>
                        no attachments
                      </span>
                    )}
                  </div>
                  {component.attachments.map((attachment) => {
                    const attachmentId = `${inputId}-${attachment.name}`;
                    return (
                      <div
                        key={attachment.name}
                        className={`${styles.componentItem} ${styles.attachmentItem}`}
                      >
                        <Checkbox
                          inputId={attachmentId}
                          checked={checked.has(attachment.name)}
                          onChange={(e) =>
                            toggleAttachment(attachment.name, !!e.checked)
                          }
                        />
                        <label
                          htmlFor={attachmentId}
                          className={styles.attachmentLabel}
                        >
                          {attachment.name}
                        </label>
                        {!attachment.is_parsed && (
                          <span className={styles.attachmentNote}>
                            not parsed yet
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};
